import { all, put, takeEvery, select } from 'redux-saga/effects'
import _ from 'lodash'

import config from '@config'
import { getHighestLevel } from '@global/helpers/helpers'
import { getLocale, getToken } from '@global/selectors'

import { BLOCK_DATA_LOADED } from '../../scenes/Gnosus/components/Workspace/components/HocBlock/constants'
import {
    blockSaveCustomData,
    blockDataLoadFailed,
} from '../../scenes/Gnosus/components/Workspace/components/HocBlock/actions'

import { CHANGE_BLOCK_LEVEL } from '../../scenes/Gnosus/components/Workspace/components/HocBlock/components/BlockTitle/components/LevelPicker/constants'

import { setYears } from '../FinanceTable/actions'

import { getBlockData, getBlockCustomData, getBlockCurrentStandard, getBlockCurrentCashFlow } from './selectors'

import {
    STANDARD,
    FSTMT_GRAPH_CHANGE_YEAR,
    FSTMT_CHANGE_DATA_TABLE_ACTIVE,
    FSTMT_CHANGE_CURRENT_STANDARD,
} from './constants'

import { setBlockLoadingTrue, setBlockLoadingFalse, getResultsData } from './helpers'

function* fstmtBlockLoaded(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.fstmt.key) return null

    const id = _.get(action, 'data.id', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !id || !blockKey) return null

    yield setBlockLoadingTrue(module, blockKey, id)

    const data = _.get(action, 'data.data', null) // Availability data
    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)
    const lang = config.languages[locale].requestKey
    const level = _.get(action, 'data.level', null)
    const transformedLevel = getHighestLevel(level, 10)

    // STANDARDS
    const standards = _.get(data, 'statements.standard', [])
    const currentStandard = yield select(getBlockCurrentStandard, id, module, blockKey)

    // Select standard from data that has the latest year, for levels higher than 10 ignore OFU, for level 10 select OFU standard
    // If the block already has a standard selected and the block reloads (language change for example), keep selected standard
    let standardType = null
    if (_.isNil(currentStandard) || transformedLevel < 20) {
        const maxYearStandardObj = _.find(standards, {
            maxAvailableYear: Math.max(..._.map(standards, (x) => x.maxAvailableYear)),
        })
        standardType = transformedLevel > 10 ? _.get(maxYearStandardObj, 'statementType', '') : STANDARD.OFU
    } else {
        standardType = currentStandard
    }

    // CASHFLOW
    const cashFlow = _.get(data, 'statements.cashFlow', [])
    const currentCashFlow = yield select(getBlockCurrentCashFlow, id, module, blockKey)
    // Select cashFlow from data that has the latest year
    // If the block already has a cashFlow selected and the block reloads (language change for example), keep selected cashFlow
    let cashFlowType = null
    if (_.isNil(currentCashFlow)) {
        const cashFlowObj = _.find(cashFlow, {
            maxAvailableYear: Math.max(..._.map(cashFlow, (x) => x.maxAvailableYear)),
        })
        cashFlowType = _.get(cashFlowObj, 'statementType', '')
    } else {
        cashFlowType = currentCashFlow
    }

    // RESULTS ENDPOINT REQUEST (STANDARDS)
    const resultsData = yield getResultsData(module, id, blockKey, lang, token, level, standardType)
    // CASHFLOW REQUEST
    const cashFlowData = yield getResultsData(module, id, blockKey, lang, token, level, cashFlowType)

    if (_.get(resultsData, 'response.success') || _.get(cashFlowData, 'response.success')) {
        const responseBody = _.get(resultsData, 'response.success.payload.response.body', {})
        const newCompareYear = _.get(responseBody, `statements.standard.${standardType}[0].year`, null)
        const newSampleYear = _.get(responseBody, `statements.standard.${standardType}[1].year`, null)

        const cashFlowBody = _.get(cashFlowData, 'response.success.payload.response.body', {})
        const cashFlowCompareYear = _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}[0].year`, null)
        const cashFlowSampleYear = _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}[1].year`, null)

        // Set selected years for results finance table
        yield put(setYears(id, 'results', newCompareYear, newSampleYear))
        // Set selected years for cashflow finance table
        yield put(setYears(id, 'cashFlow', cashFlowCompareYear, cashFlowSampleYear))

        yield put(
            blockSaveCustomData(module, id, blockKey, {
                results: {
                    currentStandard: standardType,
                    table: {
                        data: _.get(responseBody, `statements.standard.${standardType}`, []),
                        sectionsDefinition: _.get(responseBody, `legends.${standardType}.sections`, []),
                    },
                },
                cashFlow: {
                    currentStandard: cashFlowType,
                    table: {
                        data: _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}`, []),
                        sectionsDefinition: _.get(cashFlowBody, `legends.${cashFlowType}.sections`, []),
                    },
                },
                summary: {
                    assets: {
                        showDataTable: true,
                        year: _.get(responseBody, 'summary.assets[0].year', null),
                        data: _.get(responseBody, 'summary.assets'),
                    },
                    liabilities: {
                        showDataTable: true,
                        year: _.get(responseBody, 'summary.liabilities[0].year', null),
                        data: _.get(responseBody, 'summary.liabilities'),
                    },
                },
            })
        )
    } else {
        const errorCode = !resultsData.response.success
            ? _.get(resultsData, 'response.failed.payload.error.code', 500)
            : _.get(cashFlowData, 'response.failed.payload.error.code', 500)
        yield put(blockDataLoadFailed(module, id, blockKey, [errorCode]))
    }

    yield setBlockLoadingFalse(module, blockKey, id)

    return null
}

// Handle block level change
function* fstmtBlockLevelChanged(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.fstmt.key) return null

    const id = _.get(action, 'data.id', null)
    const module = _.get(action, 'data.module', null)
    const level = _.get(action, 'data.value', null)
    if (!module || !id || !blockKey || !level) return null

    yield setBlockLoadingTrue(module, blockKey, id)

    const data = yield select(getBlockData, id, module, blockKey)
    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)
    const lang = config.languages[locale].requestKey
    const transformedLevel = getHighestLevel(level, 10)

    // STANDARDS
    const standards = _.get(data, 'statements.standard', [])
    // Select standard from data that has the latest year, for levels higher than 10 ignore OFU, for level 10 select OFU standard
    const maxYearStandardObj = _.find(standards, {
        maxAvailableYear: Math.max(..._.map(standards, (x) => x.maxAvailableYear)),
    })
    const standardType = transformedLevel > 10 ? _.get(maxYearStandardObj, 'statementType', '') : STANDARD.OFU

    // CASHFLOW
    const cashFlow = _.get(data, 'statements.cashFlow', [])
    // Select cashFlow from data that has the latest year
    const cashFlowObj = _.find(cashFlow, {
        maxAvailableYear: Math.max(..._.map(cashFlow, (x) => x.maxAvailableYear)),
    })
    const cashFlowType = _.get(cashFlowObj, 'statementType', '')

    // RESULTS ENDPOINT REQUEST
    const resultsData = yield getResultsData(module, id, blockKey, lang, token, level, standardType)
    // CASHFLOW REQUEST
    const cashFlowData = yield getResultsData(module, id, blockKey, lang, token, level, cashFlowType)

    if (_.get(resultsData, 'response.success') || _.get(cashFlowData, 'response.success')) {
        const responseBody = _.get(resultsData, 'response.success.payload.response.body', {})
        const newCompareYear = _.get(responseBody, `statements.standard.${standardType}[0].year`, null)
        const newSampleYear = _.get(responseBody, `statements.standard.${standardType}[1].year`, null)

        const cashFlowBody = _.get(cashFlowData, 'response.success.payload.response.body', {})
        const cashFlowCompareYear = _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}[0].year`, null)
        const cashFlowSampleYear = _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}[1].year`, null)

        // Set selected years for results finance table
        yield put(setYears(id, 'results', newCompareYear, newSampleYear))
        // Set selected years for cashflow finance table
        yield put(setYears(id, 'cashFlow', cashFlowCompareYear, cashFlowSampleYear))

        yield put(
            blockSaveCustomData(module, id, blockKey, {
                results: {
                    currentStandard: standardType,
                    table: {
                        data: _.get(responseBody, `statements.standard.${standardType}`, []),
                        sectionsDefinition: _.get(responseBody, `legends.${standardType}.sections`, []),
                    },
                },
                cashFlow: {
                    currentStandard: cashFlowType,
                    table: {
                        data: _.get(cashFlowBody, `statements.cashFlow.${cashFlowType}`, []),
                        sectionsDefinition: _.get(cashFlowBody, `legends.${cashFlowType}.sections`, []),
                    },
                },
            })
        )
    } else {
        const errorCode = _.get(resultsData, 'response.failed.payload.error.code', 500)
        yield put(blockDataLoadFailed(module, id, blockKey, [errorCode]))
    }

    yield setBlockLoadingFalse(module, blockKey, id)

    return null
}

function* fstmtStandardChange(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.fstmt.key) return null

    const id = _.get(action, 'data.id', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !id || !blockKey) return null

    yield setBlockLoadingTrue(module, blockKey, id)

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)
    const lang = config.languages[locale].requestKey
    const level = _.get(action, 'data.level', null)
    const tableType = _.get(action, 'data.tableType', '')
    const standardType = _.get(action, 'data.standardType', '')
    const selector = tableType === 'results' ? 'standard' : 'cashFlow'

    // REQUEST (the request is the same for both standards and cashflow - results endpoint)
    const resultsData = yield getResultsData(module, id, blockKey, lang, token, level, standardType)

    if (resultsData.response.success) {
        const blockCustomData = yield select(getBlockCustomData, id, module, blockKey)

        const responseBody = _.get(resultsData, 'response.success.payload.response.body', {})
        const newCompareYear = _.get(responseBody, `statements.${selector}.${standardType}[0].year`, null)
        const newSampleYear = _.get(responseBody, `statements.${selector}.${standardType}[1].year`, null)

        // Set selected years for finance table
        yield put(setYears(id, tableType, newCompareYear, newSampleYear))

        yield put(
            blockSaveCustomData(module, id, blockKey, {
                ...blockCustomData,
                [tableType]: {
                    ...blockCustomData[tableType],
                    currentStandard: standardType,
                    table: {
                        data: _.get(responseBody, `statements.${selector}.${standardType}`, []),
                        sectionsDefinition: _.get(responseBody, `legends.${standardType}.sections`, []),
                    },
                },
            })
        )
    } else {
        const errorCode = _.get(resultsData, 'response.failed.payload.error.code', 500)
        yield put(blockDataLoadFailed(module, id, blockKey, [errorCode]))
    }

    yield setBlockLoadingFalse(module, blockKey, id)

    return null
}

function* fstmtChangeCustomData(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.fstmt.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = _.get(action, 'data.customData', null)

    yield put(blockSaveCustomData(module, id, blockKey, customData))

    return null
}

// Saga is yielded in "src/scenes/saga"
export default function* fstmtSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, fstmtBlockLoaded),
        takeEvery(CHANGE_BLOCK_LEVEL, fstmtBlockLevelChanged),
        takeEvery(FSTMT_CHANGE_CURRENT_STANDARD, fstmtStandardChange),
        takeEvery(FSTMT_GRAPH_CHANGE_YEAR, fstmtChangeCustomData),
        takeEvery(FSTMT_CHANGE_DATA_TABLE_ACTIVE, fstmtChangeCustomData),
    ])
}
